import React, { useRef } from 'react';
import { Container, Card, Alert } from 'react-bootstrap';
import { Button } from '@devesharp/react/web';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import Style from './EstablishmentIdezPage.style';
import { LoadingPage } from '~/components';
import { CNPJAndCPFMask } from '~/services';
import { useEstablishmentIdezPage } from '~/pages/EstablishmentIdez/EstablishmentIdezPage.controller';
import { Input } from '~/components/Customs/Input';
import { Select } from '~/components/Customs/Select';
import { SelectAutoComplete } from '~/components/Customs/SelectAutoComplete';

export const EstablishmentIdezPage: React.FunctionComponent<any> = () => {
   const formRef = useRef<FormHandles>(null);
   const ctrl = useEstablishmentIdezPage(formRef);

   if (ctrl.loadingCompany) {
      return (
         <Style>
            <LoadingPage />
         </Style>
      );
   }

   const readonly = ctrl.readonlyFields;

   return (
      <Style>
         <div className="title-page">
            <div className="container">
               <div className="p-3">
                  <div className="row">
                     <div className="col">
                        <h3 className="m-0 mb-1">
                           {ctrl.hasIdezAccount ? 'Gerenciar conta Idez' : 'Cadastrar conta na Idez'}
                        </h3>
                        <p className="text-muted mb-0">
                           {ctrl.hasIdezAccount
                              ? 'Acompanhe a biometria e envie o contrato social quando aplicável.'
                              : 'Complete os dados cadastrais Idez. A validação de identidade será feita pela biometria após a criação da conta.'}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {ctrl.saving && <LoadingPage />}

         <div className="container-form m-auto">
            {ctrl.company ? (
               <Card className="mb-3 p-3">
                  <strong>{readonly.name ?? ctrl.company.name ?? ctrl.company.business_name}</strong>
                  <div className="text-muted">
                     {readonly.type_name ?? (ctrl.isBusiness ? 'Pessoa jurídica' : 'Pessoa física')}
                     {readonly.document
                        ? ` · ${CNPJAndCPFMask(readonly.document)}`
                        : ctrl.company.document
                        ? ` · ${CNPJAndCPFMask(ctrl.company.document)}`
                        : ''}
                  </div>
               </Card>
            ) : null}

            <Card className="mb-3 p-3">
               <div className="form-title mb-3">Dados do estabelecimento (somente leitura)</div>
               <div className="readonly-grid">
                  <div className="readonly-item">
                     <span className="readonly-label">Nome</span>
                     {readonly.name}
                  </div>
                  <div className="readonly-item">
                     <span className="readonly-label">Documento</span>
                     {readonly.document ? CNPJAndCPFMask(readonly.document) : '-'}
                  </div>
                  <div className="readonly-item">
                     <span className="readonly-label">E-mail</span>
                     {readonly.email ?? '-'}
                  </div>
                  <div className="readonly-item">
                     <span className="readonly-label">Telefone</span>
                     {readonly.phone ?? '-'}
                  </div>
                  <div className="readonly-item">
                     <span className="readonly-label">Endereço</span>
                     {readonly.address_summary ?? '-'}
                  </div>
                  {ctrl.isBusiness && readonly.owner ? (
                     <>
                        <div className="readonly-item">
                           <span className="readonly-label">Sócio administrador</span>
                           {readonly.owner.name ?? '-'}
                        </div>
                        <div className="readonly-item">
                           <span className="readonly-label">CPF do sócio</span>
                           {readonly.owner.document ? CNPJAndCPFMask(readonly.owner.document) : '-'}
                        </div>
                     </>
                  ) : null}
               </div>
            </Card>

            {ctrl.hasIdezAccount ? (
               <Alert variant="info" className="mb-3">
                  A identidade (selfie e documento) é coletada pela WebView de biometria da Idez. Use os botões abaixo
                  para validar ou renovar a sessão.
               </Alert>
            ) : (
               <Alert variant="warning" className="mb-3">
                  {ctrl.documentsHint}
               </Alert>
            )}

            {ctrl.hasIdezAccount && !ctrl.accountIsActive && ctrl.idezBiometrics.length > 0 ? (
               <Card className="mb-3 p-3">
                  <div className="form-title mb-3">Validação de identidade (Biometria)</div>
                  {ctrl.idezBiometrics.map((entity: any) => (
                     <div className="mb-3 pb-3 border-bottom" key={entity.entity_id}>
                        <strong>{entity.name ?? 'Titular'}</strong>
                        {entity.document ? (
                           <div className="text-muted">
                              CPF: {entity.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                           </div>
                        ) : null}
                        <div className="mt-1">{ctrl.biometricsStatusLabel(entity)}</div>
                        {entity.token_expires_at ? (
                           <div className="text-muted small">
                              Token válido até: {new Date(entity.token_expires_at).toLocaleString('pt-BR')}
                           </div>
                        ) : null}
                        <div className="mt-2">
                           {entity.can_validate && entity.biometrics_url ? (
                              <Button variant="primary" onClick={() => ctrl.validateBiometrics(entity)}>
                                 Validar KYC
                              </Button>
                           ) : null}
                           {entity.needs_resend ? (
                              <>
                                 &nbsp;
                                 <Button
                                    variant="secondary"
                                    disabled={ctrl.resendingEntity === entity.entity_id}
                                    onClick={() => ctrl.resendBiometrics(entity)}
                                 >
                                    {ctrl.resendingEntity === entity.entity_id
                                       ? 'Renovando...'
                                       : 'Renovar validação'}
                                 </Button>
                              </>
                           ) : null}
                        </div>
                     </div>
                  ))}
               </Card>
            ) : null}

            <Form ref={formRef} onSubmit={ctrl.onSubmit} initialData={{ is_pep: false, is_foreign: false }}>
               <Card className="p-n">
                  {!ctrl.hasIdezAccount ? (
                     <>
                        <div className="form-padding">
                           <div className="form-title">Credenciais Idez</div>
                           <br />
                           <div className="row field-box">
                              <div className="col-md-6">
                                 <Input name="password" title="Senha de acesso Idez" type="password" />
                              </div>
                              <div className="col-md-6">
                                 <Input name="pin" title="PIN de operações (4 dígitos)" />
                              </div>
                           </div>
                        </div>

                        <hr />

                        <div className="form-padding">
                           <div className="form-title">
                              {ctrl.isBusiness ? 'Dados complementares da empresa' : 'Dados complementares PF'}
                           </div>
                           <br />

                           {ctrl.isBusiness ? (
                              <>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Input name="legal_nature" title="Natureza jurídica" />
                                    </div>
                                    <div className="col-md-4">
                                       <Select
                                          name="establishment_type"
                                          title="Tipo de estabelecimento"
                                          options={ctrl.formInfoData.establishmentTypes}
                                          onChange={ctrl.onEstablishmentTypeChange}
                                       />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="main_cnae" title="CNAE principal" />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Input name="revenue" title="Faturamento anual" />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="payroll_day" title="Dia da folha" />
                                    </div>
                                 </div>

                                 <hr />

                                 <div className="form-subtitle pb-2">KYC do sócio administrador</div>
                                 <div className="row field-box">
                                    <div className="col-md-12">
                                       <Input name="partner_mother_name" title="Nome da mãe do sócio" />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Select
                                          name="partner_identity_type"
                                          title="Tipo do documento"
                                          options={ctrl.formInfoData.identityTypes}
                                       />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="partner_identity_number" title="Número do documento" />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="partner_identity_issued_at" title="Data de emissão" />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Input name="partner_monthly_income" title="Renda mensal do sócio" />
                                    </div>
                                 </div>
                              </>
                           ) : (
                              <>
                                 <div className="row field-box">
                                    <div className="col-md-12">
                                       <Input name="mother_name" title="Nome da mãe" />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-12">
                                       <SelectAutoComplete
                                          name="occupation_id"
                                          title="Profissão"
                                          options={ctrl.formInfoData.occupations}
                                       />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Select
                                          name="identity_type"
                                          title="Tipo do documento"
                                          options={ctrl.formInfoData.identityTypes}
                                       />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="identity_number" title="Número do documento" />
                                    </div>
                                    <div className="col-md-4">
                                       <Input name="identity_issued_at" title="Data de emissão" />
                                    </div>
                                 </div>
                                 <div className="row field-box">
                                    <div className="col-md-4">
                                       <Input name="monthly_income" title="Renda mensal" />
                                    </div>
                                 </div>
                              </>
                           )}
                        </div>

                        <hr />
                     </>
                  ) : null}

                  {ctrl.groupedDocumentFields.length > 0 ? (
                     <div className="form-padding">
                        <div className="form-title">Contrato social</div>
                        <p className="text-muted">
                           {ctrl.hasIdezAccount
                              ? 'Envie ou substitua o contrato social (PJ, exceto MEI).'
                              : 'Opcional antes da criação; pode ser enviado depois.'}
                        </p>
                        {ctrl.groupedDocumentFields.map((section: any) => (
                           <div className="mb-4" key={section.key}>
                              <div className="form-subtitle pb-2">{section.title}</div>
                              <div className="row field-box">
                                 {section.fields.map((field: any) => {
                                    const hasPending = Boolean(field.pending);
                                    const hasExisting = Boolean(field.existing);
                                    const className = [
                                       'document-upload',
                                       'mb-3',
                                       'col-md-12',
                                       hasPending || hasExisting ? 'has-file' : '',
                                       field.error ? 'has-error' : '',
                                    ]
                                       .filter(Boolean)
                                       .join(' ');

                                    return (
                                       <div className={className} key={field.type}>
                                          <span className="document-upload__label">{field.label}</span>
                                          {hasExisting && !hasPending ? (
                                             <div className="document-upload__status">
                                                {field.existing.upload_status === 'failed'
                                                   ? `Falhou: ${field.existing.idez_upload_error ?? 'erro no envio'}`
                                                   : `Enviado: ${field.existing.original_name ?? field.type}`}
                                             </div>
                                          ) : null}
                                          {hasPending ? (
                                             <div className="document-upload__status">
                                                Selecionado: {field.pending.name}
                                             </div>
                                          ) : null}
                                          <input
                                             type="file"
                                             accept="image/png,image/jpeg,image/jpg,image/bmp,image/webp,image/heic,image/heif,application/pdf"
                                             onChange={(event) =>
                                                ctrl.onDocumentChange(field.type, event.target.files?.[0] ?? null)
                                             }
                                          />
                                          {field.error ? (
                                             <div className="document-upload__error">{field.error}</div>
                                          ) : null}
                                       </div>
                                    );
                                 })}
                              </div>
                           </div>
                        ))}
                     </div>
                  ) : null}
               </Card>

               {(ctrl.hasIdezAccount && ctrl.groupedDocumentFields.length > 0) || !ctrl.hasIdezAccount ? (
                  <div className="text-right pt-2">
                     <Button size="lg" type="submit">
                        <b>
                           {ctrl.hasIdezAccount
                              ? 'Enviar contrato social'
                              : 'Criar conta Idez'}
                        </b>
                     </Button>
                  </div>
               ) : null}
               <br />
               <br />
            </Form>
         </div>
         <Container />
      </Style>
   );
};
